import { useContext, useMemo, useCallback } from 'react';
import { useProjectTimezone } from 'store/project';
import { SxProps, Theme, Box } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { LocalizationProvider } from '@mui/x-date-pickers-pro/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { DateRangePickerToolbar } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { ReactElement } from 'react';

import { IntlContext } from 'intl/IntlProviderWrapper';
import {
  startOfDayToUtcMillis,
  endOfDayToUtcMillis,
  millisToDateTime,
  dateTimeToTimezone,
} from 'shared/utils/date/dates';
import { DateRangeValue } from './types';

export interface DateRangePickerBaseProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  normalizeTwoDigitYearTo2000?: boolean;
  /**
   * Minimum selectable date
   */
  minDate?: DateTime;
  /**
   * Maximum selectable date
   */
  maxDate?: DateTime;
  /**
   * Format for displaying dates
   * @default undefined (uses locale default)
   */
  format?: string;
  /**
   * Available views for the calendar picker
   * @default ['day', 'month', 'year']
   */
  views?: ('year' | 'month' | 'day')[];
  /**
   * The initial view to show when the picker opens
   * @default 'day'
   */
  openTo?: 'year' | 'month' | 'day';
  /**
   * Size of the date input fields
   * @default 'small'
   */
  fieldSize?: 'small' | 'medium';
}

export function DateRangePickerBase({
  value,
  onChange,
  disabled = false,
  sx,
  normalizeTwoDigitYearTo2000 = false,
  minDate,
  maxDate,
  format,
  views = ['day', 'month', 'year'],
  openTo = 'day',
  fieldSize = 'small',
}: DateRangePickerBaseProps): ReactElement {
  const timezone = useProjectTimezone();
  const { calendarLocales } = useContext(IntlContext);

  const startDate = millisToDateTime(value.start, timezone);
  const endDate = millisToDateTime(value.end, timezone);

  // Convert minDate/maxDate to timezone if provided
  const minDateInTimezone = useMemo(() => {
    if (!minDate) return undefined;
    const zoned = dateTimeToTimezone(minDate, timezone);
    return zoned ?? undefined;
  }, [minDate, timezone]);

  const maxDateInTimezone = useMemo(() => {
    if (!maxDate) return undefined;
    const zoned = dateTimeToTimezone(maxDate, timezone);
    return zoned ?? undefined;
  }, [maxDate, timezone]);

  const normalizeTwoDigitYear = useCallback(
    (date: DateTime | null): DateTime | null => {
      if (!normalizeTwoDigitYearTo2000 || !date || !date.isValid) {
        return date;
      }

      if (date.year >= 0 && date.year <= 99) {
        return date.set({ year: date.year + 2000 });
      }

      return date;
    },
    [normalizeTwoDigitYearTo2000]
  );

  const handleChange = useCallback(
    (newValue: [DateTime | null, DateTime | null]): void => {
      const [rawStart, rawEnd] = newValue;

      const start = normalizeTwoDigitYear(rawStart);
      const end = normalizeTwoDigitYear(rawEnd);

      const zonedStart = dateTimeToTimezone(start, timezone);
      const zonedEnd = dateTimeToTimezone(end, timezone);

      const nextValue: DateRangeValue = {
        start: startOfDayToUtcMillis(zonedStart),
        end: endOfDayToUtcMillis(zonedEnd),
      };

      if (
        nextValue.start === value.start &&
        nextValue.end === value.end
      ) {
        return;
      }

      onChange(nextValue);
    },
    [
      normalizeTwoDigitYear,
      onChange,
      timezone,
      value.start,
      value.end,
    ]
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={calendarLocales.code}
    >
      <DateRangePicker
        value={[startDate, endDate]}
        onChange={handleChange}
        timezone={timezone}
        disabled={disabled}
        sx={sx}
        minDate={minDateInTimezone}
        maxDate={maxDateInTimezone}
        format={format}
        // @ts-ignore - views and openTo props are available in MUI X v7 but types may not be updated
        views={views}
        // @ts-ignore
        openTo={openTo}
        slots={{
          field: MultiInputDateRangeField,
          toolbar: DateRangePickerToolbar,
        }}
        slotProps={{
          textField: {
            size: fieldSize,
            variant: 'outlined',
            fullWidth: true,
          },
          actionBar: { actions: ['clear', 'cancel', 'today', 'accept'] },
        }}
      />
    </LocalizationProvider>
  );
}

